import useSWR, { type SWRConfiguration } from 'swr';
import denApi from '@/lib/api/denApi';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';

// API key info returned by the server (never includes the actual key)
export type ApiKeyInfo = {
  exists: boolean;
  prefix?: string;
  createdAt?: string;
  createdByUserName?: string;
};

type ApiKeyInfoResult = {
  apiKeyInfo: ApiKeyInfo;
  error: Error | undefined;
  isLoading: boolean;
  refresh: () => void;
};

const fetcher = async (url: string) => await denApi.get<ApiKeyInfo, void>(url);

/**
 * Fetches the API key info for an organization.
 * Returns metadata about the key (if it exists) but never the key itself.
 *
 * @param organizationId - The organization ID to fetch the API key info for
 * @param swrOptions - Optional SWR configuration overrides
 * @returns The API key info, or { exists: false } if no key exists
 */
export function useApiKeyInfo(
  organizationId: string | null,
  swrOptions?: SWRConfiguration<ApiKeyInfo>
): ApiKeyInfoResult {
  const { organization } = useMyOrganization();
  // Org context comes from x-current-org-id cookie (not URL params)
  // Pause requests until admin access to the current organization is confirmed.
  const url =
    organizationId &&
    organization?.id === organizationId &&
    organization.isAdmin
      ? `admin/organization/api-key`
      : null;

  const { data, error, isLoading, mutate } = useSWR<ApiKeyInfo>(url, fetcher, {
    revalidateOnFocus: false,
    ...(swrOptions ?? {}),
  });

  return {
    apiKeyInfo: data ?? { exists: false },
    error,
    isLoading,
    refresh: mutate,
  };
}
